<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import {
  ref,
  inject,
  watch,
  onMounted,
  onUnmounted,
  computed,
  nextTick,
  type Ref,
  type ShallowRef,
} from 'vue'

import { deleteRoutePointAction } from '@/services/routePointActions'
import { isRoutingPending, spinningRoutePoints } from '@/services/routingService'
import { useUiStore } from '@/stores/uiStore'
import type { Tour } from '@/types/tour'
import { createMapMarker } from '@/utils/mapMarkerElement'
import { computeRoutePointLabels, getRoutePointLabelInGroup } from '@/utils/routePointGrouping'
import { getTrackRoutePointEntries } from '@/utils/trackRoutePoints'

import {
  createPreviewRoutePointMarkerElement,
  createRoutePointMarkerElement,
  createPendingRoutePointMarkerElement,
  createSpinningRoutePointMarkerElement,
} from './routePointMarkerElement'

const props = defineProps<{
  tour: Tour
  activeTrackClientId?: string | null
  pendingRoutePoints?: { lat: number; lon: number }[]
  previewRoutePoints?: { lat: number; lon: number }[]
}>()

const emit = defineEmits<{
  'route-point-click': [tourId: string, routePointIndex: number]
  'route-point-dragend': [tourId: string, routePointIndex: number, newLat: number, newLon: number]
  'route-point-deleted': [tourId: string, routePointIndex: number]
  'pending-route-point-dragend': [pendingIndex: number, newLat: number, newLon: number]
  'pending-route-point-deleted': [pendingIndex: number]
}>()

const uiStore = useUiStore()
const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded')

const sourceId = computed(() => `route-point-source-${props.tour.clientId}`)
const lineSourceId = computed(() => `route-point-line-source-${props.tour.clientId}`)
const lineLayerId = computed(() => `route-point-line-${props.tour.clientId}`)
const dragGuideSourceId = computed(() => `route-point-drag-guide-source-${props.tour.clientId}`)
const dragGuideLayerId = computed(() => `route-point-drag-guide-${props.tour.clientId}`)
// Invisible hit-test layer for unclustered points
const pointLayerId = computed(() => `route-point-hit-${props.tour.clientId}`)

type SimpleMarker = {
  remove: () => void
  getLngLat: () => { lng: number; lat: number }
  setLngLat: (lngLat: [number, number]) => SimpleMarker
}
const markers = ref<SimpleMarker[]>([])
const spinnerMarkers = ref<SimpleMarker[]>([])

const MIN_DRAG_DISTANCE_PX = 8

type MarkerLngLat = {
  lng: number
  lat: number
}

type DragGuideState = {
  current: MarkerLngLat
  previous: MarkerLngLat | null
  next: MarkerLngLat | null
}

const getPixelDistance = (from: MarkerLngLat, to: MarkerLngLat): number => {
  const map = mapInstance?.value
  if (!map) return Number.POSITIVE_INFINITY

  const fromPoint = map.project([from.lng, from.lat])
  const toPoint = map.project([to.lng, to.lat])
  const deltaX = toPoint.x - fromPoint.x
  const deltaY = toPoint.y - fromPoint.y

  return Math.hypot(deltaX, deltaY)
}

const activeDragGuide = ref<DragGuideState | null>(null)

const getDragGuideGeoJsonData = (): GeoJSON.FeatureCollection => {
  const dragGuide = activeDragGuide.value
  if (!dragGuide) {
    return { type: 'FeatureCollection', features: [] }
  }

  const features: GeoJSON.Feature<GeoJSON.LineString>[] = []
  if (dragGuide.previous) {
    features.push({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [dragGuide.previous.lng, dragGuide.previous.lat],
          [dragGuide.current.lng, dragGuide.current.lat],
        ],
      },
    })
  }
  if (dragGuide.next) {
    features.push({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [dragGuide.current.lng, dragGuide.current.lat],
          [dragGuide.next.lng, dragGuide.next.lat],
        ],
      },
    })
  }

  return { type: 'FeatureCollection', features }
}

const syncDragGuideSource = () => {
  const map = mapInstance?.value
  if (!map || !map.getSource(dragGuideSourceId.value)) return
  const source = map.getSource(dragGuideSourceId.value) as maplibregl.GeoJSONSource
  source.setData(getDragGuideGeoJsonData())
}

const startDragGuide = (
  current: MarkerLngLat,
  previous: MarkerLngLat | null,
  next: MarkerLngLat | null,
) => {
  activeDragGuide.value = {
    current: { ...current },
    previous: previous ? { ...previous } : null,
    next: next ? { ...next } : null,
  }
  closeContextMenu()
  syncDragGuideSource()
}

const updateDragGuide = (current: MarkerLngLat) => {
  if (!activeDragGuide.value) return
  activeDragGuide.value = {
    ...activeDragGuide.value,
    current: { ...current },
  }
  syncDragGuideSource()
}

const clearDragGuide = () => {
  if (!activeDragGuide.value) return
  activeDragGuide.value = null
  syncDragGuideSource()
}

const bindMarkerHover = (touchArea: HTMLDivElement, setHovered: (hovered: boolean) => void) => {
  touchArea.addEventListener('mouseenter', () => {
    setHovered(true)
  })
  touchArea.addEventListener('mouseleave', () => {
    setHovered(false)
  })
}

const bindTouchContextMenu = (
  touchArea: HTMLDivElement,
  setHovered: (hovered: boolean) => void,
  onTap: (clientX: number, clientY: number) => void,
) => {
  let activeTouchId: number | null = null
  let startX = 0
  let startY = 0
  let touchMoved = false

  const resetTouchState = () => {
    activeTouchId = null
    touchMoved = false
    setHovered(false)
    touchArea.style.opacity = '1'
  }

  const resolveTrackedTouch = (touches: TouchList) => {
    if (activeTouchId === null) return null
    return Array.from(touches).find((touch) => touch.identifier === activeTouchId) ?? null
  }

  touchArea.addEventListener('touchstart', (event) => {
    const touch = event.changedTouches[0]
    if (!touch) return

    activeTouchId = touch.identifier
    startX = touch.clientX
    startY = touch.clientY
    touchMoved = false

    setHovered(true)
    touchArea.style.opacity = '0.7'
  })

  touchArea.addEventListener('touchmove', (event) => {
    const touch = resolveTrackedTouch(event.touches)
    if (!touch) return

    if (Math.hypot(touch.clientX - startX, touch.clientY - startY) >= MIN_DRAG_DISTANCE_PX) {
      touchMoved = true
      setHovered(false)
      touchArea.style.opacity = '1'
    }
  })

  touchArea.addEventListener('touchend', (event) => {
    const touch = resolveTrackedTouch(event.changedTouches)
    if (!touch) {
      resetTouchState()
      return
    }

    const shouldOpenMenu = !touchMoved
    resetTouchState()
    if (shouldOpenMenu) {
      event.preventDefault()
      event.stopPropagation()
      suppressNextMapClick()
      onTap(touch.clientX, touch.clientY)
    }
  })

  touchArea.addEventListener('touchcancel', () => {
    resetTouchState()
  })
}

const bindMarkerDrag = (
  marker: maplibregl.Marker,
  initialLngLat: MarkerLngLat,
  {
    previous,
    next,
    onValidDragEnd,
  }: {
    previous: MarkerLngLat | null
    next: MarkerLngLat | null
    onValidDragEnd: (lngLat: MarkerLngLat) => void
  },
) => {
  let dragOrigin = { ...initialLngLat }

  marker.on('dragstart', () => {
    dragOrigin = { ...marker.getLngLat() }
    startDragGuide(dragOrigin, previous, next)
  })

  marker.on('drag', () => {
    updateDragGuide(marker.getLngLat())
  })

  marker.on('dragend', () => {
    const lngLat = marker.getLngLat()
    if (getPixelDistance(dragOrigin, lngLat) < MIN_DRAG_DISTANCE_PX) {
      marker.setLngLat([dragOrigin.lng, dragOrigin.lat])
      clearDragGuide()
      return
    }

    clearDragGuide()
    marker.setLngLat([dragOrigin.lng, dragOrigin.lat])
    onValidDragEnd(lngLat)
  })
}

// Context menu state. `kind` distinguishes persisted vs pending so delete
// routes to the right handler (persisted → deleteRoutePointAction, pending
// → emit pending-route-point-deleted).
const contextMenu = ref<{
  visible: boolean
  x: number
  y: number
  tourId: string
  routePointIndex: number
  kind: 'persisted' | 'pending'
}>({
  visible: false,
  x: 0,
  y: 0,
  tourId: '',
  routePointIndex: -1,
  kind: 'persisted',
})

const openContextMenu = (
  tourId: string,
  index: number,
  clientX: number,
  clientY: number,
  kind: 'persisted' | 'pending' = 'persisted',
) => {
  const map = mapInstance?.value
  if (!map) return
  const rect = map.getContainer().getBoundingClientRect()
  contextMenu.value = {
    visible: true,
    x: clientX - rect.left,
    y: clientY - rect.top,
    tourId,
    routePointIndex: index,
    kind,
  }
}

const closeContextMenu = () => {
  contextMenu.value = { ...contextMenu.value, visible: false }
}

const suppressNextMapClick = () => {
  uiStore.suppressNextMapClick()
}

const handleContextMenuDelete = async () => {
  const { tourId, routePointIndex, kind } = contextMenu.value
  closeContextMenu()
  if (kind === 'pending') {
    emit('pending-route-point-deleted', routePointIndex)
    return
  }
  const success = await deleteRoutePointAction(tourId, routePointIndex)
  if (success) {
    emit('route-point-deleted', tourId, routePointIndex)
  }
}

const contextMenuAbsolutePos = computed(() => {
  const map = mapInstance?.value
  if (!map) return { x: 0, y: 0 }
  const rect = map.getContainer().getBoundingClientRect()
  return {
    x: rect.left + contextMenu.value.x,
    y: rect.top + contextMenu.value.y,
  }
})

const { isTrackDrawingActive, isTrackEditMode, editingTrackTourId, editingTrackClientId } =
  storeToRefs(uiStore)

const isPersistedRoutePointDraggable = (routePointTrackClientId?: string | null): boolean =>
  (isTrackDrawingActive.value &&
    !!props.activeTrackClientId &&
    (routePointTrackClientId ?? null) === props.activeTrackClientId) ||
  (isTrackEditMode.value &&
    editingTrackTourId.value === props.tour.clientId &&
    (routePointTrackClientId ?? null) === (editingTrackClientId.value ?? null))

const getVisibleRoutePointEntries = () => {
  if (
    isTrackEditMode.value &&
    editingTrackTourId.value === props.tour.clientId &&
    editingTrackClientId.value
  ) {
    return getTrackRoutePointEntries(props.tour.routePoints ?? [], editingTrackClientId.value)
  }

  if (isTrackDrawingActive.value && props.activeTrackClientId) {
    return getTrackRoutePointEntries(props.tour.routePoints ?? [], props.activeTrackClientId)
  }

  return []
}

const getVisibleRoutePoints = () => getVisibleRoutePointEntries().map((entry) => entry.routePoint)

const getGeoJsonData = (): GeoJSON.FeatureCollection => {
  const rps = getVisibleRoutePoints()
  const labels = computeRoutePointLabels(rps)
  return {
    type: 'FeatureCollection',
    features: rps.map((rp, index) => ({
      type: 'Feature',
      properties: {
        id: `${props.tour.clientId}_rp_${index}`,
        tourId: props.tour.clientId,
        routePointIndex: index,
        routePointName: rp.name || `Route Point ${labels.get(rp.clientId) ?? String(index)}`,
        order: rp.order,
      },
      geometry: {
        type: 'Point',
        coordinates: [rp.lon, rp.lat],
      },
    })),
  } as GeoJSON.FeatureCollection
}

const getLineGeoJsonData = (): GeoJSON.FeatureCollection => {
  const rps = getVisibleRoutePoints()
  if (rps.length < 2 || (props.tour.tracks?.length ?? 0) > 0) {
    return { type: 'FeatureCollection', features: [] }
  }
  const sorted = [...rps].sort((a, b) => a.order - b.order)
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: sorted.map((rp) => [rp.lon, rp.lat]),
        },
      },
    ],
  }
}

const createMarkerElement = createRoutePointMarkerElement
const createPendingMarkerElement = createPendingRoutePointMarkerElement
const createPreviewMarkerElement = createPreviewRoutePointMarkerElement
const createSpinningMarkerElement = createSpinningRoutePointMarkerElement

const removeCustomMarkers = () => {
  markers.value.forEach((marker) => marker.remove())
  markers.value = []
}

const removeSpinnerMarkers = () => {
  spinnerMarkers.value.forEach((marker) => marker.remove())
  spinnerMarkers.value = []
}

const isActiveRoutingTour = (): boolean =>
  (isTrackEditMode.value &&
    editingTrackTourId.value === props.tour.clientId &&
    !!editingTrackClientId.value) ||
  isTrackDrawingActive.value

const isRoutePointTourVisible = (): boolean => props.tour.visible !== false || isActiveRoutingTour()

const isVisible = (): boolean => {
  return (
    isRoutePointTourVisible() &&
    uiStore.areRoutePointsVisible &&
    !uiStore.areMapElementsForceHidden &&
    !uiStore.isInWorkspacesOverview &&
    isActiveRoutingTour()
  )
}

const addSource = () => {
  const map = mapInstance?.value
  if (!map) return

  if (map.getSource(sourceId.value)) {
    const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
    source.setData(getGeoJsonData())
  } else {
    map.addSource(sourceId.value, {
      type: 'geojson',
      data: getGeoJsonData(),
    })
  }

  // Line source for connecting route points when no tracks
  if (map.getSource(lineSourceId.value)) {
    const source = map.getSource(lineSourceId.value) as maplibregl.GeoJSONSource
    source.setData(getLineGeoJsonData())
  } else {
    map.addSource(lineSourceId.value, {
      type: 'geojson',
      data: getLineGeoJsonData(),
    })
  }

  if (map.getSource(dragGuideSourceId.value)) {
    const source = map.getSource(dragGuideSourceId.value) as maplibregl.GeoJSONSource
    source.setData(getDragGuideGeoJsonData())
  } else {
    map.addSource(dragGuideSourceId.value, {
      type: 'geojson',
      data: getDragGuideGeoJsonData(),
    })
  }
}

const createLayers = () => {
  const map = mapInstance?.value
  if (!map) return

  // Dashed connecting line (rendered when no tracks exist)
  if (!map.getLayer(lineLayerId.value)) {
    map.addLayer({
      id: lineLayerId.value,
      type: 'line',
      source: lineSourceId.value,
      paint: {
        'line-color': '#3B82F6',
        'line-width': 2,
        'line-dasharray': [4, 4],
        'line-opacity': 0.7,
      },
    })
  }

  if (!map.getLayer(dragGuideLayerId.value)) {
    map.addLayer({
      id: dragGuideLayerId.value,
      type: 'line',
      source: dragGuideSourceId.value,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#3B82F6',
        'line-width': 3,
        'line-dasharray': [2, 2],
        'line-opacity': 0.9,
      },
    })
  }

  // Invisible circle layer for hit-testing
  if (!map.getLayer(pointLayerId.value)) {
    map.addLayer({
      id: pointLayerId.value,
      type: 'circle',
      source: sourceId.value,
      paint: {
        'circle-color': '#3B82F6',
        'circle-radius': 14,
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
      },
    })
  }
}

const setupEventHandlers = () => {
  const map = mapInstance?.value
  if (!map) return

  map.on('mouseenter', pointLayerId.value, () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = 'pointer'
  })

  map.on('mouseleave', pointLayerId.value, () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = ''
  })
}

const updateLayerVisibility = () => {
  const map = mapInstance?.value
  if (!map) return

  const visible = isVisible()
  const visibility = visible ? 'visible' : 'none'

  if (map.getLayer(lineLayerId.value)) {
    map.setLayoutProperty(lineLayerId.value, 'visibility', visibility)
  }

  if (map.getLayer(dragGuideLayerId.value)) {
    map.setLayoutProperty(dragGuideLayerId.value, 'visibility', visibility)
  }

  if (map.getLayer(pointLayerId.value)) {
    map.setLayoutProperty(pointLayerId.value, 'visibility', visibility)
  }

  if (visible) {
    debouncedAddMarkers()
    syncSpinnerMarkers()
  } else {
    clearMarkerRecreationTimeout()
    removeCustomMarkers()
    removeSpinnerMarkers()
    clearDragGuide()
  }
}

const removeLayers = () => {
  const map = mapInstance?.value
  if (!map) return

  if (map.getLayer(pointLayerId.value)) {
    map.removeLayer(pointLayerId.value)
  }

  if (map.getLayer(dragGuideLayerId.value)) {
    map.removeLayer(dragGuideLayerId.value)
  }

  if (map.getLayer(lineLayerId.value)) {
    map.removeLayer(lineLayerId.value)
  }

  if (map.getSource(sourceId.value)) {
    map.removeSource(sourceId.value)
  }

  if (map.getSource(dragGuideSourceId.value)) {
    map.removeSource(dragGuideSourceId.value)
  }

  if (map.getSource(lineSourceId.value)) {
    map.removeSource(lineSourceId.value)
  }
}

const addMarkers = () => {
  const map = mapInstance?.value
  if (!map) return

  removeCustomMarkers()

  if (!isVisible()) return

  const visibleEntries = getVisibleRoutePointEntries()
  const rps = visibleEntries.map((entry) => entry.routePoint)
  const pending = props.pendingRoutePoints ?? []
  const preview = props.previewRoutePoints ?? []
  const direction = isTrackEditMode.value ? uiStore.editingTrackAppendDirection : 'end'
  const activeTrackClientId = isTrackEditMode.value
    ? (uiStore.editingTrackClientId ?? null)
    : (props.activeTrackClientId ?? null)

  // For prepend: pending markers come first, then existing
  // For append: existing markers come first, then pending
  const prependPending = direction === 'start' ? pending : []
  const appendPending = direction === 'end' ? pending : []

  // Compute per-track group size for the editing track (existing + pending
  // treated as part of the editing track's group).
  const existingLabels = computeRoutePointLabels(rps)
  const editingTrackExistingCount = rps.filter(
    (rp) => (rp.trackClientId ?? null) === activeTrackClientId,
  ).length
  const editingTrackGroupSize = editingTrackExistingCount + pending.length

  const labelForPendingPrepend = (i: number): string =>
    getRoutePointLabelInGroup(i, editingTrackGroupSize)
  const labelForExistingInEditingTrack = (existingIndexInGroup: number): string =>
    getRoutePointLabelInGroup(prependPending.length + existingIndexInGroup, editingTrackGroupSize)
  const labelForPendingAppend = (i: number): string =>
    getRoutePointLabelInGroup(
      prependPending.length + editingTrackExistingCount + i,
      editingTrackGroupSize,
    )

  // Helper: per-track running index for existing RPs (only used to decide
  // label in the editing track).

  // Pending markers should look and behave exactly like persisted ones — same
  // click/touch → context menu, draggable via the same MapLibre marker API.
  // Only difference is the amber dashed style + new emit names.
  const renderPendingMarker = (
    rp: { lat: number; lon: number },
    pendingIndex: number,
    label: string,
    previous: MarkerLngLat | null,
    next: MarkerLngLat | null,
  ) => {
    const { element, touchArea, setHovered } = createPendingMarkerElement(label)
    touchArea.dataset.routePointKind = 'pending'
    touchArea.dataset.routePointIndex = String(pendingIndex)
    bindMarkerHover(touchArea, setHovered)

    touchArea.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      suppressNextMapClick()
      openContextMenu(props.tour.clientId, pendingIndex, event.clientX, event.clientY, 'pending')
    })
    bindTouchContextMenu(touchArea, setHovered, (clientX, clientY) => {
      openContextMenu(props.tour.clientId, pendingIndex, clientX, clientY, 'pending')
    })

    const marker = createMapMarker({ element, anchor: 'center', draggable: true })
      .setLngLat([rp.lon, rp.lat])
      .addTo(map)

    bindMarkerDrag(
      marker,
      { lng: rp.lon, lat: rp.lat },
      {
        previous,
        next,
        onValidDragEnd: (lngLat) => {
          emit('pending-route-point-dragend', pendingIndex, lngLat.lat, lngLat.lng)
        },
      },
    )

    markers.value.push(marker as SimpleMarker)
  }

  const renderPreviewMarker = (rp: { lat: number; lon: number }, previewIndex: number) => {
    const { element } = createPreviewMarkerElement(String(previewIndex + 1))
    const marker = createMapMarker({ element, anchor: 'center', draggable: false })
      .setLngLat([rp.lon, rp.lat])
      .addTo(map)

    markers.value.push(marker as SimpleMarker)
  }

  const orderedMarkers: Array<
    | {
        kind: 'pending'
        rp: { lat: number; lon: number }
        pendingIndex: number
        label: string
      }
    | {
        kind: 'persisted'
        rp: (typeof visibleEntries)[number]['routePoint']
        globalIndex: number
        label: string
      }
  > = []

  prependPending.forEach((rp, i) => {
    orderedMarkers.push({
      kind: 'pending',
      rp,
      pendingIndex: i,
      label: labelForPendingPrepend(i),
    })
  })

  let runningEditingTrackIndex = 0
  visibleEntries.forEach(({ routePoint: rp, globalIndex }, index) => {
    const rpTrackId = rp.trackClientId ?? null
    let label: string
    if (pending.length > 0 && rpTrackId === activeTrackClientId) {
      label = labelForExistingInEditingTrack(runningEditingTrackIndex)
      runningEditingTrackIndex++
    } else {
      label = existingLabels.get(rp.clientId) ?? String(index)
    }
    orderedMarkers.push({
      kind: 'persisted',
      rp,
      globalIndex,
      label,
    })
  })

  appendPending.forEach((rp, i) => {
    orderedMarkers.push({
      kind: 'pending',
      rp,
      pendingIndex: prependPending.length + i,
      label: labelForPendingAppend(i),
    })
  })

  orderedMarkers.forEach((markerConfig, markerIndex) => {
    const previousConfig = markerIndex > 0 ? orderedMarkers[markerIndex - 1] : null
    const nextConfig =
      markerIndex < orderedMarkers.length - 1 ? orderedMarkers[markerIndex + 1] : null
    const previous = previousConfig
      ? { lng: previousConfig.rp.lon, lat: previousConfig.rp.lat }
      : null
    const next = nextConfig ? { lng: nextConfig.rp.lon, lat: nextConfig.rp.lat } : null

    if (markerConfig.kind === 'pending') {
      renderPendingMarker(
        markerConfig.rp,
        markerConfig.pendingIndex,
        markerConfig.label,
        previous,
        next,
      )
      return
    }

    const { element, touchArea, setHovered } = createMarkerElement(markerConfig.label)
    touchArea.dataset.routePointKind = 'persisted'
    touchArea.dataset.routePointIndex = String(markerConfig.globalIndex)
    bindMarkerHover(touchArea, setHovered)

    touchArea.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      suppressNextMapClick()
      // While drawing, tapping a marker must not extend the track (handled above), but it should
      // still open the context menu so via-points can be deleted mid-draw — same as pending markers.
      openContextMenu(props.tour.clientId, markerConfig.globalIndex, event.clientX, event.clientY)
      emit('route-point-click', props.tour.clientId, markerConfig.globalIndex)
    })
    bindTouchContextMenu(touchArea, setHovered, (clientX, clientY) => {
      openContextMenu(props.tour.clientId, markerConfig.globalIndex, clientX, clientY)
      emit('route-point-click', props.tour.clientId, markerConfig.globalIndex)
    })

    const isDraggable = isPersistedRoutePointDraggable(markerConfig.rp.trackClientId)
    const marker = createMapMarker({
      element,
      anchor: 'center',
      draggable: isDraggable,
    })
      .setLngLat([markerConfig.rp.lon, markerConfig.rp.lat])
      .addTo(map)

    if (isDraggable) {
      bindMarkerDrag(
        marker,
        { lng: markerConfig.rp.lon, lat: markerConfig.rp.lat },
        {
          previous,
          next,
          onValidDragEnd: (lngLat) => {
            emit(
              'route-point-dragend',
              props.tour.clientId,
              markerConfig.globalIndex,
              lngLat.lat,
              lngLat.lng,
            )
          },
        },
      )
    }

    markers.value.push(marker as SimpleMarker)
  })

  preview.forEach((rp, index) => {
    renderPreviewMarker(rp, index)
  })
}

const syncSpinnerMarkers = () => {
  removeSpinnerMarkers()

  const map = mapInstance?.value
  if (!map || !isVisible() || !isRoutingPending.value || spinningRoutePoints.value.length === 0) {
    return
  }

  spinnerMarkers.value = spinningRoutePoints.value.map((spinnerPoint) => {
    const element = createSpinningMarkerElement()
    return createMapMarker({ element, anchor: 'center', draggable: false })
      .setLngLat([spinnerPoint.lon, spinnerPoint.lat])
      .addTo(map) as SimpleMarker
  })
}

let markerRecreationTimeout: number | null = null
const clearMarkerRecreationTimeout = () => {
  if (markerRecreationTimeout) {
    clearTimeout(markerRecreationTimeout)
    markerRecreationTimeout = null
  }
}

const debouncedAddMarkers = () => {
  clearMarkerRecreationTimeout()
  markerRecreationTimeout = window.setTimeout(() => {
    addMarkers()
    syncSpinnerMarkers()
    markerRecreationTimeout = null
  }, 200)
}

const forceRemoveMarkers = () => {
  clearMarkerRecreationTimeout()
  removeCustomMarkers()
  removeSpinnerMarkers()
  clearDragGuide()
}

defineExpose({
  forceRemoveMarkers,
})

const eventHandlers = {
  moveend: null as (() => void) | null,
  zoomend: null as (() => void) | null,
  click: null as (() => void) | null,
}

const initializeLayers = () => {
  const map = mapInstance?.value
  const loaded = mapLoaded?.value

  if (!map || !loaded) return

  addSource()
  createLayers()
  setupEventHandlers()
  updateLayerVisibility()

  if (isVisible()) {
    addMarkers()
    syncSpinnerMarkers()
  }

  eventHandlers.moveend = debouncedAddMarkers
  eventHandlers.zoomend = debouncedAddMarkers
  eventHandlers.click = () => {
    if (uiStore.shouldSuppressNextMapClick()) return
    closeContextMenu()
  }

  map.on('moveend', eventHandlers.moveend)
  map.on('zoomend', eventHandlers.zoomend)
  map.on('movestart', closeContextMenu)
  map.on('click', eventHandlers.click)
}

onMounted(() => {
  watch(
    mapLoaded ?? ref(false),
    (isLoaded) => {
      if (isLoaded && mapInstance?.value) {
        initializeLayers()
      }
    },
    { immediate: true },
  )
})

watch(
  () => props.tour.visible,
  () => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()
    }
  },
)

watch(
  () => uiStore.areRoutePointsVisible,
  (newVisible, oldVisible) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      if (newVisible && !oldVisible && isRoutePointTourVisible()) {
        nextTick(() => {
          debouncedAddMarkers()
        })
      }
    }
    if (!newVisible) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => uiStore.areMapElementsForceHidden,
  (newHidden, oldHidden) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      if (!newHidden && oldHidden && uiStore.areRoutePointsVisible && isRoutePointTourVisible()) {
        nextTick(() => {
          debouncedAddMarkers()
        })
      }
    }
    if (newHidden) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => uiStore.isInWorkspacesOverview,
  (newInOverview, oldInOverview) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility()

      if (
        !newInOverview &&
        oldInOverview &&
        uiStore.areRoutePointsVisible &&
        isRoutePointTourVisible()
      ) {
        nextTick(() => {
          debouncedAddMarkers()
        })
      }
    }
    if (newInOverview && !oldInOverview) {
      forceRemoveMarkers()
    }
  },
)

watch(
  [
    () => uiStore.isTrackDrawingActive,
    () => uiStore.isTrackEditMode,
    () => uiStore.editingTrackTourId,
    () => uiStore.editingTrackClientId,
    () => props.activeTrackClientId,
  ],
  () => {
    if (mapInstance?.value && mapLoaded?.value) {
      if (mapInstance.value.getSource(sourceId.value)) {
        const source = mapInstance.value.getSource(sourceId.value) as maplibregl.GeoJSONSource
        source.setData(getGeoJsonData())
      }
      if (mapInstance.value.getSource(lineSourceId.value)) {
        const source = mapInstance.value.getSource(lineSourceId.value) as maplibregl.GeoJSONSource
        source.setData(getLineGeoJsonData())
      }
      updateLayerVisibility()

      if (isVisible()) {
        nextTick(() => {
          debouncedAddMarkers()
        })
      }
    }

    if (!isVisible()) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => props.tour.routePoints,
  () => {
    const map = mapInstance?.value
    if (map && mapLoaded?.value) {
      if (map.getSource(sourceId.value)) {
        const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
        source.setData(getGeoJsonData())
      }
      if (map.getSource(lineSourceId.value)) {
        const source = map.getSource(lineSourceId.value) as maplibregl.GeoJSONSource
        source.setData(getLineGeoJsonData())
      }

      nextTick(() => {
        if (isVisible()) {
          debouncedAddMarkers()
        }
      })
    }
  },
  { deep: true },
)

watch(
  () => props.pendingRoutePoints,
  () => {
    if (mapInstance?.value && mapLoaded?.value && isVisible()) {
      nextTick(() => {
        debouncedAddMarkers()
      })
    }
  },
  { deep: true },
)

watch(
  () => props.previewRoutePoints,
  () => {
    if (mapInstance?.value && mapLoaded?.value && isVisible()) {
      nextTick(() => {
        debouncedAddMarkers()
      })
    }
  },
  { deep: true },
)

watch(
  [() => spinningRoutePoints.value, () => isRoutingPending.value],
  () => {
    if (!mapInstance?.value || !mapLoaded?.value) return

    syncSpinnerMarkers()
    if (isVisible()) {
      debouncedAddMarkers()
    }
  },
  { deep: true },
)

watch(
  () => props.tour.tracks?.length ?? 0,
  () => {
    // Update connecting line visibility when tracks change
    const map = mapInstance?.value
    if (map && mapLoaded?.value && map.getSource(lineSourceId.value)) {
      const source = map.getSource(lineSourceId.value) as maplibregl.GeoJSONSource
      source.setData(getLineGeoJsonData())
    }
  },
)

onUnmounted(() => {
  forceRemoveMarkers()

  const map = mapInstance?.value
  if (map) {
    if (eventHandlers.moveend) map.off('moveend', eventHandlers.moveend)
    if (eventHandlers.zoomend) map.off('zoomend', eventHandlers.zoomend)
    if (eventHandlers.click) map.off('click', eventHandlers.click)
    map.off('movestart', closeContextMenu)
  }

  removeLayers()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="contextMenu.visible"
      class="fixed inset-0 z-[9998]"
      @click="closeContextMenu"
      @contextmenu.prevent="closeContextMenu"
    ></div>
    <div
      v-if="contextMenu.visible"
      data-testid="route-point-context-menu"
      class="fixed bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[160px] z-[9999]"
      :style="{
        left: `${contextMenuAbsolutePos.x}px`,
        top: `${contextMenuAbsolutePos.y}px`,
      }"
      @click.stop
    >
      <button
        type="button"
        data-testid="route-point-context-menu-delete"
        class="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 hover:cursor-pointer"
        @click="handleContextMenuDelete"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 6h18"></path>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
        Delete route point
      </button>
    </div>
  </Teleport>
  <div class="hidden"></div>
</template>

<style scoped>
:global(.route-point-marker-container:hover .route-point-marker) {
  border-color: #2563eb !important;
}
</style>

<style>
/* Spinning animation for in-progress route point marker during routing */
.route-point-spinning-marker {
  animation: route-point-spin 1s linear infinite;
}
@keyframes route-point-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
